import * as React from 'react'
import { getRepoSpaceName } from '@basehub/data/replicache/index'
import { useQuery } from '@tanstack/react-query'
import { invariant } from '@epic-web/invariant'
import { getCommitBlockTreeManagerQueryFn } from './query'
import { useSelectedTeamAndRepo } from '../use-selected-team-and-repo'
import { useRepoReplicache } from '../use-repo-replicache'

export const useCommitBlockTreeManager = ({
  commitId,
  rootBlockId,
  keepPreviousData,
}: {
  commitId: string | undefined
  rootBlockId: string | undefined
  keepPreviousData?: boolean
  caller?: string
}) => {
  const { data } = useSelectedTeamAndRepo()
  const { data: repoReplicacheData } = useRepoReplicache()
  const teamId = data?.team?.id
  const repoId = data?.repo?.id

  const queryKey = React.useMemo(
    () => getCommitBlockTreeManagerQueryKey({ commitId, teamId, repoId }),
    [commitId, repoId, teamId]
  )

  const queryResult = useQuery(
    queryKey,
    async () => {
      invariant(teamId && repoId && commitId && rootBlockId, 'not enabled')

      const result = await getCommitBlockTreeManagerQueryFn({
        replicache: repoReplicacheData?.replicache,
        spaceId: getRepoSpaceName(teamId, repoId),
        commitId,
        rootBlockId,
      })

      return result
    },
    {
      enabled: Boolean(teamId && repoId && commitId && rootBlockId),
      staleTime: Infinity, // immutable
      refetchOnWindowFocus: false,
      networkMode: 'always',
      keepPreviousData,
    }
  )

  if (queryResult.error) {
    console.error(queryResult.error)
  }

  return queryResult
}

export const getCommitBlockTreeManagerQueryKey = ({
  commitId,
  teamId,
  repoId,
}: {
  commitId: string | undefined
  teamId: string | undefined
  repoId: string | undefined
}) => {
  return ['commit-tree', commitId, teamId, repoId] as const
}
